/**
 * Mark ledger rows withdrawn when their object leaves the board.
 * Same matching rules as the server revoke: source, about-target, and relation ends.
 */

function idAliases(id) {
  const raw = String(id || '').trim();
  if (!raw) return [];
  const aliases = [raw];
  if (raw.startsWith('shape:frame-')) {
    aliases.push(raw.slice('shape:frame-'.length));
  } else if (raw.startsWith('frame:')) {
    aliases.push(raw.slice('frame:'.length));
  } else if (!raw.startsWith('shape:') && !raw.startsWith('pin:')) {
    aliases.push(`shape:frame-${raw}`);
  }
  return aliases.filter(Boolean);
}

function collectDeletedIds(activityIds = [], shapeIds = []) {
  const ids = new Set();
  const add = (value) => {
    for (const alias of idAliases(value)) ids.add(alias);
  };
  for (const id of activityIds) add(id);
  for (const id of shapeIds) add(id);
  return ids;
}

function touchesDeleted(constraint, revokedIds) {
  const fields = [
    constraint?.sourcePinId,
    constraint?.sourceShapeId,
    constraint?.aboutPinId,
    constraint?.aboutShapeId,
    constraint?.fromId,
    constraint?.toId,
  ];
  return fields.some(value => idAliases(value).some(alias => revokedIds.has(alias)));
}

export function withdrawConstraintsForDeletedSources(constraints = [], {
  activityIds = [],
  shapeIds = [],
} = {}) {
  const list = Array.isArray(constraints) ? constraints : [];
  const revokedIds = collectDeletedIds(activityIds, shapeIds);
  if (!revokedIds.size) return list;
  return list.map((constraint) => {
    if (!constraint || constraint.active === false) return constraint;
    if (!touchesDeleted(constraint, revokedIds)) return constraint;
    return { ...constraint, active: false, withdrawn: true };
  });
}

function ledgerRowText(constraint) {
  return String(constraint?.claim || constraint?.value || '').trim();
}

/** Header label: active claims and constraints tied to this shape, joined. */
export function ledgerHeaderTextForShape(constraints, shape) {
  return ledgerRowsRemovedWithShape(constraints, shape)
    .map(ledgerRowText)
    .filter(Boolean)
    .join(' · ');
}

/**
 * Active ledger rows delete would turn off for these ids.
 * Same source / about-target / relation-end match as withdraw.
 */
export function ledgerRowsRemovedWithSources(constraints = [], {
  activityIds = [],
  shapeIds = [],
} = {}) {
  const list = Array.isArray(constraints) ? constraints : [];
  const revokedIds = collectDeletedIds(activityIds, shapeIds);
  if (!revokedIds.size) return [];
  const seen = new Set();
  const rows = [];
  for (const constraint of list) {
    if (!constraint || constraint.active === false) continue;
    if (!touchesDeleted(constraint, revokedIds)) continue;
    const text = ledgerRowText(constraint);
    if (!text) continue;
    const kind = String(constraint.kind || 'fact').trim().toLowerCase();
    const key = constraint.id
      ? `id:${constraint.id}`
      : `${kind}|${text.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(constraint);
  }
  return rows;
}

/** Ledger rows that leave the board when this shape is deleted. */
export function ledgerRowsRemovedWithShape(constraints = [], shape) {
  if (!shape) return [];
  const activityIds = [];
  const activityId = shape.meta?.activityId;
  if (activityId != null && String(activityId).trim()) {
    activityIds.push(String(activityId).trim());
  }
  const shapeIds = shape.id ? [shape.id] : [];
  return ledgerRowsRemovedWithSources(constraints, { activityIds, shapeIds });
}

export function markDigestDeleted(pendingDigest = {}) {
  const digest = pendingDigest && typeof pendingDigest === 'object' ? pendingDigest : {};
  return {
    ...digest,
    needsStepRefresh: true,
    deletedSinceStepRefresh: true,
  };
}
